import { Grid } from '@material-ui/core';
import { pick } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { isManualTareLineItem } from 'src/common/helpers/action-window-helper';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { itemMeasureAttributes } from 'src/common/helpers/item-quantity-measure';
import {
  IElectronicJournalRecord,
  TbrData,
} from 'src/common/interfaces/states/IElectronicJournalRecord';
import { AppState, store } from 'src/common/redux/configure-store';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import { styles } from './weight-info-styles';

const WeightInfo: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();

  const { selectedItem } = props;

  const item = useSelector((state: AppState) =>
    state.itemList.items?.find(i => i.lineId === selectedItem.SequenceNumber),
  );
  const returnItem = useSelector((state: AppState) =>
    state.itemList.returns?.find(i => i.lineId === selectedItem.SequenceNumber),
  );

  const lineItem = selectedItem.Type === EJRecordTypes.ReturnItem ? returnItem : item;

  const tbrData: TbrData = pick(selectedItem, [
    'tbrReturnItem',
    'tbrReturnQty',
    'tbrAvailableQty',
    'tbrScanData',
  ]);

  if (!lineItem) {
    return <></>;
  }

  const { baseUnit, atomicMsuUnit, atomicMsuPrice, atomicMsuUnitForPrice } = itemMeasureAttributes(
    lineItem,
    tbrData,
  );

  const isLinkedItemTare = lineItem.linkedLines?.filter(
    i => i.parentLineLinkType === 'Tare' || i.itemType === 'Tare',
  ).length;
  const isManualTareItem = isManualTareLineItem(lineItem);

  const isTBRActive: boolean =
    store.getState().posMode.returnMode.returnType === ReturnsType.TransactionBasedReturn;

  return (
    <Grid container data-testid='customer-display-weight-info'>
      <Grid item xs={10} className={classes.lineItem}>
        <span className={classes.lineItemBlackText} data-testid='customer-display-base-unit-text'>
          {baseUnit} {atomicMsuUnit}
        </span>
        <span className={classes.lineItemBlackText} data-testid='customer-display-quantifier-text'>
          {isLinkedItemTare || isManualTareItem ? ` ${t('TareNet')} ` : ' '}
        </span>
        <span
          className={classes.lineItemBlackText}
          data-testid='customer-display-item-price-per-unit'
        >
          x {atomicMsuPrice}{' '}
        </span>
        <span className={classes.lineItem} data-testid='customer-display-unit-text'>
          / {atomicMsuUnitForPrice}
          {atomicMsuUnit}
        </span>
      </Grid>
      {!!item?.linkedLines?.length && !isTBRActive && (
        <Grid item xs={2} className={classes.price}>
          <span className={classes.lineItem} data-testid='customer-display-price'>
            {convertCurrency(lineItem.extendedAmount || 0)}
          </span>
        </Grid>
      )}
    </Grid>
  );
};

interface Props {
  selectedItem: IElectronicJournalRecord;
}

export default WeightInfo;
